// 用戶行為追蹤和分析工具
import { trackEvent, trackUserJourney, trackError, trackPerformance } from './analytics';
import { logFirebaseEvent, logUserJourney, logError, logPerformance, setUserProperty } from './firebaseAnalytics';

// 用戶會話管理
class UserSession {
  private sessionId: string;
  private startTime: number;
  private pageViews: string[] = [];
  private interactions: any[] = [];
  private errors: any[] = [];

  constructor() {
    this.sessionId = this.generateSessionId();
    this.startTime = Date.now();
    this.trackSessionStart();
  }

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private trackSessionStart() {
    const sessionData = {
      session_id: this.sessionId,
      start_time: new Date().toISOString(),
      user_agent: navigator.userAgent,
      screen_resolution: `${window.screen.width}x${window.screen.height}`,
      viewport_size: `${window.innerWidth}x${window.innerHeight}`,
      referrer: document.referrer,
      language: navigator.language,
    };

    // GA4 追蹤
    trackUserJourney('session_start', sessionData);
    
    // Firebase 追蹤
    logFirebaseEvent('session_start', sessionData);
  }

  // 追蹤頁面瀏覽
  trackPageView(pageName: string, additionalData?: Record<string, any>) {
    const pageViewData = {
      page_name: pageName,
      page_url: window.location.href,
      page_title: document.title,
      timestamp: new Date().toISOString(),
      time_on_page: this.getTimeOnPreviousPage(),
      ...additionalData,
    };

    this.pageViews.push(pageName);
    
    // GA4 追蹤
    trackEvent('page_view', pageViewData);
    
    // Firebase 追蹤
    logFirebaseEvent('page_view', pageViewData);
  }

  // 追蹤用戶交互
  trackInteraction(interactionType: string, target: string, additionalData?: Record<string, any>) {
    const interactionData = {
      interaction_type: interactionType,
      target_element: target,
      timestamp: new Date().toISOString(),
      session_duration: this.getSessionDuration(),
      ...additionalData,
    };

    this.interactions.push(interactionData);
    
    // GA4 追蹤
    trackEvent('user_interaction', interactionData);
    
    // Firebase 追蹤
    logFirebaseEvent('user_interaction', interactionData);
  }

  // 追蹤錯誤
  trackError(errorType: string, errorMessage: string, errorLocation?: string) {
    const errorData = {
      error_type: errorType,
      error_message: errorMessage,
      error_location: errorLocation,
      timestamp: new Date().toISOString(),
      session_duration: this.getSessionDuration(),
      page_views_count: this.pageViews.length,
      interactions_count: this.interactions.length,
    };

    this.errors.push(errorData);
    
    // GA4 追蹤
    trackError(errorType, errorMessage, errorLocation);
    
    // Firebase 追蹤
    logError(errorType, errorMessage, errorData);
  }

  // 追蹤性能指標
  trackPerformanceMetric(metricName: string, value: number, unit?: string) {
    // GA4 追蹤
    trackPerformance(metricName, value, unit);
    
    // Firebase 追蹤
    logPerformance(metricName, value, unit);
  }

  // 追蹤用戶旅程步驟
  trackJourneyStep(step: string, stepData?: Record<string, any>) {
    const journeyData = {
      step_name: step,
      step_number: this.interactions.length + 1,
      timestamp: new Date().toISOString(),
      session_duration: this.getSessionDuration(),
      previous_step: this.getLastJourneyStep(),
      ...stepData,
    };

    // GA4 追蹤
    trackUserJourney(step, journeyData);
    
    // Firebase 追蹤
    logUserJourney(step, journeyData);
  }

  // 結束會話
  endSession() {
    const sessionData = {
      session_id: this.sessionId,
      duration: this.getSessionDuration(),
      page_views: this.pageViews,
      interactions_count: this.interactions.length,
      errors_count: this.errors.length,
      end_time: new Date().toISOString(),
    };

    // GA4 追蹤
    trackUserJourney('session_end', sessionData);
    
    // Firebase 追蹤
    logFirebaseEvent('session_end', sessionData);
  }

  // 獲取會話持續時間
  private getSessionDuration(): number {
    return Date.now() - this.startTime;
  }

  // 獲取在上一頁的時間
  private getTimeOnPreviousPage(): number {
    // 簡化實現，實際可以更複雜
    return this.getSessionDuration();
  }

  // 獲取最後一個旅程步驟
  private getLastJourneyStep(): string {
    return this.interactions.length > 0 ? this.interactions[this.interactions.length - 1].interaction_type : 'session_start';
  }

  // 獲取會話摘要
  getSessionSummary() {
    return {
      sessionId: this.sessionId,
      duration: this.getSessionDuration(),
      pageViews: this.pageViews.length,
      interactions: this.interactions.length,
      errors: this.errors.length,
      startTime: new Date(this.startTime).toISOString(),
    };
  }
}

// 全局會話實例
let currentSession: UserSession | null = null;

// 初始化用戶行為追蹤
export const initUserBehaviorTracking = () => {
  if (typeof window === 'undefined') return;
  
  currentSession = new UserSession();
  
  // 追蹤頁面可見性變化
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      trackPageVisibility('hidden');
    } else {
      trackPageVisibility('visible');
    }
  });

  // 追蹤頁面卸載
  window.addEventListener('beforeunload', () => {
    if (currentSession) {
      currentSession.endSession();
    }
  });

  // 追蹤性能指標
  if ('performance' in window) {
    window.addEventListener('load', () => {
      setTimeout(() => {
        trackPageLoadPerformance();
      }, 1000);
    });
  }
};

// 追蹤頁面可見性
const trackPageVisibility = (visibility: string) => {
  if (currentSession) {
    currentSession.trackInteraction('page_visibility_change', visibility, {
      visibility_state: visibility,
    });
  }
};

// 追蹤頁面加載性能
const trackPageLoadPerformance = () => {
  if (!('performance' in window) || !currentSession) return;

  const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
  
  if (navigation) {
    currentSession.trackPerformanceMetric('page_load_time', navigation.loadEventEnd - navigation.fetchStart);
    currentSession.trackPerformanceMetric('dom_content_loaded', navigation.domContentLoadedEventEnd - navigation.fetchStart);
    currentSession.trackPerformanceMetric('first_paint', navigation.responseEnd - navigation.fetchStart);
  }
};

// 導出追蹤函數
export const trackUserBehavior = {
  // 頁面瀏覽
  pageView: (pageName: string, additionalData?: Record<string, any>) => {
    if (currentSession) {
      currentSession.trackPageView(pageName, additionalData);
    }
  },

  // 用戶交互
  interaction: (type: string, target: string, additionalData?: Record<string, any>) => {
    if (currentSession) {
      currentSession.trackInteraction(type, target, additionalData);
    }
  },

  // 錯誤追蹤
  error: (errorType: string, errorMessage: string, errorLocation?: string) => {
    if (currentSession) {
      currentSession.trackError(errorType, errorMessage, errorLocation);
    }
  },

  // 性能指標
  performance: (metricName: string, value: number, unit?: string) => {
    if (currentSession) {
      currentSession.trackPerformanceMetric(metricName, value, unit);
    }
  },

  // 旅程步驟
  journeyStep: (step: string, stepData?: Record<string, any>) => {
    if (currentSession) {
      currentSession.trackJourneyStep(step, stepData);
    }
  },

  // 獲取會話摘要
  getSessionSummary: () => {
    return currentSession?.getSessionSummary() || null;
  },
};

// 特定功能追蹤
export const trackSpecificBehaviors = {
  // AI聊天追蹤
  aiChat: {
    messageSent: (messageLength: number, hasPolicies: boolean) => {
      trackUserBehavior.interaction('ai_message_sent', 'ai_chat_input', {
        message_length: messageLength,
        has_policies: hasPolicies,
        interaction_type: 'ai_chat',
      });
    },
    
    responseReceived: (responseTime: number, responseLength: number) => {
      trackUserBehavior.interaction('ai_response_received', 'ai_chat_response', {
        response_time: responseTime,
        response_length: responseLength,
        interaction_type: 'ai_chat',
      });
    },
  },

  // 保單管理追蹤
  policyManagement: {
    categorySelected: (category: string, policyCount: number) => {
      trackUserBehavior.interaction('category_selected', 'category_button', {
        selected_category: category,
        policy_count: policyCount,
        interaction_type: 'policy_management',
      });
    },
    
    policyViewed: (policyId: string, policyName: string) => {
      trackUserBehavior.interaction('policy_viewed', 'policy_card', {
        policy_id: policyId,
        policy_name: policyName,
        interaction_type: 'policy_management',
      });
    },
  },

  // 轉換追蹤
  conversion: {
    waitlistJoined: (source: string, hasUsedAI: boolean) => {
      trackUserBehavior.interaction('waitlist_joined', 'join_waitlist_button', {
        conversion_source: source,
        has_used_ai: hasUsedAI,
        interaction_type: 'conversion',
      });
    },
  },
};

// 用戶屬性設置
export const setUserAttributes = {
  // 設置用戶類型
  userType: (type: 'new' | 'returning' | 'power_user') => {
    setUserProperty('user_type', type);
  },

  // 設置設備類型
  deviceType: (type: 'mobile' | 'tablet' | 'desktop') => {
    setUserProperty('device_type', type);
  },

  // 設置使用頻率
  usageFrequency: (frequency: 'low' | 'medium' | 'high') => {
    setUserProperty('usage_frequency', frequency);
  },
};

export default trackUserBehavior;
